import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useTenant } from '../../tenant/useTenant'
import { profileLoaded, restoreSession, signedOut, type CustomerProfile } from '../../features/auth/authSlice'
import { getToken } from '../../services/tokenStorage'
import { useGetProfileQuery } from '../../services/accountApi'

export interface Session {
  slug: string
  /** A stored, unexpired token for THIS slug. Tokens are namespaced per vendor. */
  isAuthenticated: boolean
  customer: CustomerProfile | null
  /** Token present but the profile has not come back yet. */
  loading: boolean
}

/**
 * H2 — the single place the SPA decides whether somebody is signed in.
 *
 * The token in localStorage is the source of truth, read on every render rather
 * than mirrored into a ref: it is what `baseApi` puts on the wire, and reading it
 * directly means the guard never redirects on a first frame that has not caught up
 * with Redux yet. Redux only mirrors it so the shell's account icon re-renders.
 *
 * A stale token is not an error state. `baseApi` already clears it on a 401, so all
 * this has to do is reflect that as "signed out" and let the guard send the visitor
 * to sign in — never an error screen for something the customer cannot act on.
 */
export function useSession(): Session {
  const { slug } = useTenant()
  const dispatch = useAppDispatch()
  const customer = useAppSelector((s) => s.auth.customer)

  useEffect(() => {
    dispatch(restoreSession(slug))
  }, [slug, dispatch])

  const hasToken = getToken(slug) !== null

  const { data, error, isLoading } = useGetProfileQuery(slug, { skip: !hasToken || customer !== null })

  useEffect(() => {
    if (data) dispatch(profileLoaded(data))
  }, [data, dispatch])

  useEffect(() => {
    if (error) dispatch(signedOut(slug))
  }, [error, slug, dispatch])

  return {
    slug,
    isAuthenticated: hasToken,
    customer,
    loading: hasToken && customer === null && isLoading,
  }
}
